import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import Input from '@tds/core-input';
import Button from '@tds/core-button';
import Notification from '@tds/core-notification';
import Select from 'react-select';

import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { FormContainer, Label } from './css';

const ServiceForm = ({
  businessConnectServices,
  setBusinessConnectServices,
  servicesList,
  editService,
  setEditService,
  setIsFormTouched
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    formButtons: { saveButtonLabel, cancelButtonLabel },
    assigneeSRTModal: { addButton },
    callCentre: {
      labelServices,
      tableHeader: {
        labelPaymentType,
        labelPrice,
        labelPriceMonthly,
        labelPriceUpfront,
        labelQuantity
      },
      placeholderService,
      placeholderPaymentType
    }
  } = localizedStrings;

  const [formValues, setFormValues] = useState({
    serviceName: '',
    paymentTypeList: [],
    paymentType: '',
    servicePrice: '',
    quantity: '',
    formError: false
  });

  const getServiceDataObject = () => {
    const services = [...businessConnectServices];
    if (editService) {
      const serviceIndex = _.findIndex(businessConnectServices, {
        id: editService
      });
      services[serviceIndex].name = formValues.serviceName.value;
      services[serviceIndex].paymentType = formValues.paymentType.value;
      services[serviceIndex].price = +formValues.servicePrice;
      services[serviceIndex].quantity = +formValues.quantity;
    } else {
      services.push({
        id: uuidv4(),
        name: formValues.serviceName.value,
        paymentType: formValues.paymentType.value,
        price: +formValues.servicePrice,
        quantity: +formValues.quantity
      });
    }

    const res = [];
    services.forEach(service => {
      const data = res.find(
        i => i.name === service.name && i.paymentType === service.paymentType
      );
      if (!data) {
        res.push({ ...service });
      } else {
        data.quantity = +data.quantity + +service.quantity;
      }
    });

    return res;
  };

  const restForm = () => {
    setFormValues({
      ...formValues,
      serviceName: '',
      paymentType: '',
      servicePrice: '',
      quantity: '',
      formError: false
    });
  };

  const addService = () => {
    if (
      !formValues.serviceName.value ||
      !formValues.paymentType.value ||
      !formValues.quantity
    )
      setFormValues({ ...formValues, formError: true });
    else {
      const servicesArr = getServiceDataObject();
      setBusinessConnectServices(servicesArr);
      setIsFormTouched(true);
      restForm();
      if (editService) setEditService('');
    }
  };

  const getPaymentTypeList = serviceNameValue => {
    const serviceValue = servicesList.find(
      service => serviceNameValue === service.value
    );

    const paymentList = [];
    if (serviceValue.monthly)
      paymentList.push({ label: 'monthly', value: 'monthly' });
    if (serviceValue.upfront)
      paymentList.push({ label: 'upfront', value: 'upfront' });
    return paymentList;
  };

  const onServiceSelect = selectedOption => {
    setFormValues({
      ...formValues,
      serviceName: selectedOption,
      paymentTypeList: [...getPaymentTypeList(selectedOption.value)],
      paymentType: '',
      servicePrice: '',
      quantity: ''
    });
  };

  const onPaymentTypeSelect = selectedOption => {
    const serviceValue = servicesList.find(
      service => formValues.serviceName.value === service.value
    );

    setFormValues({
      ...formValues,
      paymentType: selectedOption,
      servicePrice: serviceValue[selectedOption.value]
    });
  };

  const getServicesList = () => {
    const groupOptions = [];
    servicesList.forEach(({ label, value, group }) => {
      const groupIndex = _.findIndex(groupOptions, { label: group });
      if (groupIndex === -1) {
        const newGroup = {
          label: group,
          options: []
        };
        newGroup.options.push({ label, value });
        groupOptions.push(newGroup);
      } else {
        groupOptions[groupIndex].options.push({ label, value });
      }
    });
    return groupOptions;
  };

  const handleCancelEdit = () => {
    setEditService('');
    restForm();
  };

  const getPricingLabel = () => {
    const valuePaymentType = formValues.paymentType.value;
    if (valuePaymentType === '') return labelPrice;
    if (valuePaymentType === 'monthly') return labelPriceMonthly;
    if (valuePaymentType === 'upfront') return labelPriceUpfront;
    return labelPrice;
  };

  useEffect(() => {
    if (editService) {
      const serviceDetails = businessConnectServices.find(
        service => service.id === editService
      );
      const { name, paymentType: type, price, quantity: qty } = serviceDetails;
      setFormValues({
        ...formValues,
        serviceName: { label: name, value: name },
        paymentTypeList: [...getPaymentTypeList(name)],
        paymentType: { label: type, value: type },
        servicePrice: price,
        quantity: qty
      });
    }
  }, [editService]);

  return (
    <FormContainer>
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <div style={{ width: '40%' }}>
          <Label for="serviceName">{labelServices}</Label>
          <Select
            id="serviceName"
            placeholder={placeholderService}
            options={getServicesList()}
            value={formValues.serviceName}
            onChange={selectedOption => {
              onServiceSelect(selectedOption);
            }}
            theme={theme => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                primary25: '#F2EFF4',
                primary: '#4b286d'
              }
            })}
          />
        </div>
        <div style={{ width: '25%', marginLeft: '1rem' }}>
          <Label for="paymentType">{labelPaymentType}</Label>
          <Select
            id="paymentType"
            placeholder={placeholderPaymentType}
            options={formValues.paymentTypeList}
            value={formValues.paymentType}
            onChange={selectedOption => {
              onPaymentTypeSelect(selectedOption);
            }}
            isClearable={false}
            theme={theme => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                primary25: '#F2EFF4',
                primary: '#4b286d'
              }
            })}
          />
        </div>
        <div style={{ width: '15%', marginLeft: '1rem' }}>
          <Input
            id="servicePrice"
            label={getPricingLabel()}
            value={formValues.servicePrice}
            type="number"
            onChange={e => {
              setFormValues({
                ...formValues,
                servicePrice: e.target.value
              });
            }}
          />
        </div>
        <div style={{ width: '10%', marginLeft: '1rem' }}>
          <Input
            id="quantity"
            label={labelQuantity}
            value={formValues.quantity}
            type="number"
            onChange={e => {
              setFormValues({
                ...formValues,
                quantity: e.target.value
              });
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ margin: '1rem 0 0 0.5rem' }}>
          <Button onClick={addService}>
            {editService ? saveButtonLabel : addButton}
          </Button>
        </div>
        {editService && (
          <div style={{ margin: '1rem 0 0 2rem' }}>
            <Button variant="secondary" onClick={handleCancelEdit}>
              {cancelButtonLabel}
            </Button>
          </div>
        )}
      </div>
      {formValues.formError && (
        <div style={{ marginTop: '1rem' }}>
          <Notification
            variant="error"
            dismissible
            onDismiss={() =>
              setFormValues({
                ...formValues,
                formError: false
              })
            }
            copy="en"
          >
            Please select all required fields
          </Notification>
        </div>
      )}
    </FormContainer>
  );
};

ServiceForm.propTypes = {
  businessConnectServices: PropTypes.arrayOf(PropTypes.any).isRequired,
  setBusinessConnectServices: PropTypes.func.isRequired,
  servicesList: PropTypes.arrayOf(PropTypes.any).isRequired,
  editService: PropTypes.string.isRequired,
  setEditService: PropTypes.func.isRequired,
  setIsFormTouched: PropTypes.bool.isRequired
};

export default ServiceForm;
